import { Injectable, signal } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class MassAssigmentService {
  private selectedIds = new Set<number>();
  private _countSignal = signal(0);
  countSignal = this._countSignal.asReadonly();

  /**
   * Used when user use the 'select all items' button
   * - **Inclusion mode**, The items in the list are considered selected.
   * - **Exclusion mode**, All items except those in the list are considered selected.
   */
  private _exclusionMode = signal(false);
  exclusionMode = this._exclusionMode.asReadonly();

  private totalTickets = 0;

  private updateCount(): void {
    if (this._exclusionMode()) {
      this._countSignal.set(this.totalTickets - this.selectedIds.size);
    } else {
      this._countSignal.set(this.selectedIds.size);
    }
  }

  add(id: number): void {
    this.selectedIds.add(id);
    this.updateCount();
  }

  remove(id: number): void {
    this.selectedIds.delete(id);
    this.updateCount();
  }

  /**
   * Checks if a ticket is selected based on the current selection mode.
   * - In **inclusion mode**, the IDs are considered selected if they are in the list.
   * - In **exclusion mode**, the IDs are considered selected if they are NOT in the list.
   *
   * @param id The id of the ticket to be checked.
   */
  isSelected(id: number): boolean {
    if (this._exclusionMode()) {
      return !this.selectedIds.has(id);
    } else {
      return this.selectedIds.has(id);
    }
  }

  /**
   * Adds a list of ticket IDs to the current selection state.
   *
   * - In **inclusion mode**, the IDs are added to the selection set.
   * - In **exclusion mode**, the IDs are removed from the exclusion set (i.e., marked as selected).
   *
   * After processing the list, the internal selection count is updated accordingly.
   *
   * @param ids An array of ticket IDs to be added or removed based on the current selection mode.
   */
  addList(ids: number[]): void {
    if (this._exclusionMode()) {
      ids.forEach((id) => this.remove(id));
    } else {
      ids.forEach((id) => this.add(id));
    }
    this.updateCount();
  }

  /**
   * Set the selection mode to exclusion and update the total number of tickets.
   *
   * @param totalTickets The total number of tickets in the whole table.
   */
  selectAll(totalTickets: number): void {
    this._exclusionMode.set(true);
    this.totalTickets = totalTickets;
    this.selectedIds.clear();
    this.updateCount();
  }

  /**
   * Gets the current selection of ticket IDs.
   */
  getSelectedIds(): number[] {
    return Array.from(this.selectedIds);
  }

  /**
   * Clears the current selection of ticket IDs and resets the count.
   */
  reset(): void {
    this.selectedIds.clear();
    this._countSignal.set(0);
    this._exclusionMode.set(false);
    this.totalTickets = 0;
  }

  /**
   * Toggles the selection state of a item based on the current selection mode.
   *
   * - In **inclusion mode**, the item is added to the selection if it is not selected,
   *   or removed if it is already selected.
   * - In **exclusion mode**, the logic is inverted: the item is removed from the exclusion list
   *   if it is currently excluded, or added to it if it is not.
   *
   * @param itemId The ID of the item to toggle.
   */
  toggleItemSelection(itemId: number): void {
    if (this.exclusionMode()) {
      if (this.isSelected(itemId)) {
        this.add(itemId);
      } else {
        this.remove(itemId);
      }
    } else {
      if (this.isSelected(itemId)) {
        this.remove(itemId);
      } else {
        this.add(itemId);
      }
    }
  }
}
